import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const WishlistFilters = ({ 
  onFiltersChange = () => {},
  totalItems = 0,
  activeFilters = {}
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: 'dateAdded',
    priority: 'all',
    category: 'all',
    priceRange: 'all',
    compatibility: 'all',
    priceAlert: false,
    hasNote: false,
    ...activeFilters
  });

  const sortOptions = [
    { value: 'dateAdded', label: 'Date Added (Newest)' },
    { value: 'dateAddedOld', label: 'Date Added (Oldest)' },
    { value: 'priceHigh', label: 'Price (High to Low)' },
    { value: 'priceLow', label: 'Price (Low to High)' },
    { value: 'priority', label: 'Priority (High to Low)' },
    { value: 'name', label: 'Name (A-Z)' },
    { value: 'priceChange', label: 'Biggest Price Drop' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'wheels', label: 'Wheels & Tires' },
    { value: 'drivetrain', label: 'Drivetrain' },
    { value: 'brakes', label: 'Brakes' },
    { value: 'handlebars', label: 'Handlebars & Stem' },
    { value: 'saddle', label: 'Saddle & Seatpost' },
    { value: 'pedals', label: 'Pedals' },
    { value: 'accessories', label: 'Accessories' }
  ];

  const priceRangeOptions = [
    { value: 'all', label: 'All Prices' },
    { value: 'under50', label: 'Under $50' },
    { value: '50to100', label: '$50 - $100' },
    { value: '100to250', label: '$100 - $250' },
    { value: '250to500', label: '$250 - $500' },
    { value: 'over500', label: 'Over $500' }
  ];

  const compatibilityOptions = [
    { value: 'all', label: 'All Compatibility' },
    { value: 'perfect', label: 'Perfect Match' },
    { value: 'good', label: 'Good Match' },
    { value: 'adapter', label: 'Requires Adapter' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      sortBy: 'dateAdded',
      priority: 'all',
      category: 'all',
      priceRange: 'all',
      compatibility: 'all',
      priceAlert: false,
      hasNote: false
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.priority !== 'all') count++;
    if (filters?.category !== 'all') count++;
    if (filters?.priceRange !== 'all') count++;
    if (filters?.compatibility !== 'all') count++;
    if (filters?.priceAlert) count++;
    if (filters?.hasNote) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-foreground">
            Wishlist Items ({totalItems})
          </h3>
          {activeFilterCount > 0 && (
            <span className="bg-accent text-accent-foreground text-xs px-2 py-1 rounded-full">
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear
            </Button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden p-2 hover:bg-muted rounded-full transition-colors interactive-scale"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
          </button>
        </div>
      </div>
      {/* Filters Content */}
      <div className={`p-4 space-y-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        {/* Primary Sort */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Select
            label="Sort By"
            options={sortOptions}
            value={filters?.sortBy}
            onChange={(value) => handleFilterChange('sortBy', value)}
          />
          
          <Select
            label="Priority"
            options={priorityOptions}
            value={filters?.priority}
            onChange={(value) => handleFilterChange('priority', value)}
          />
        </div>

        {/* Secondary Filters */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Select
            label="Category"
            options={categoryOptions}
            value={filters?.category}
            onChange={(value) => handleFilterChange('category', value)}
          />
          
          <Select
            label="Price Range"
            options={priceRangeOptions}
            value={filters?.priceRange}
            onChange={(value) => handleFilterChange('priceRange', value)}
          />
          
          <Select
            label="Compatibility"
            options={compatibilityOptions}
            value={filters?.compatibility}
            onChange={(value) => handleFilterChange('compatibility', value)}
          />
        </div>

        {/* Checkbox Filters */}
        <div className="flex flex-wrap gap-4 pt-2 border-t border-border">
          <Checkbox
            label="Price alerts enabled"
            checked={filters?.priceAlert}
            onChange={(e) => handleFilterChange('priceAlert', e?.target?.checked)}
          />
          <Checkbox
            label="Has personal notes"
            checked={filters?.hasNote}
            onChange={(e) => handleFilterChange('hasNote', e?.target?.checked)}
          />
        </div>

        {/* Quick Filter Buttons */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          <Button
            variant={filters?.priority === 'high' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('priority', filters?.priority === 'high' ? 'all' : 'high')}
          >
            High Priority Only
          </Button>
          <Button
            variant={filters?.priceRange === 'under50' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('priceRange', filters?.priceRange === 'under50' ? 'all' : 'under50')}
          >
            Under $50
          </Button>
          <Button
            variant={filters?.compatibility === 'perfect' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('compatibility', filters?.compatibility === 'perfect' ? 'all' : 'perfect')}
          >
            Perfect Match
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WishlistFilters;